import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { forwardRef, createContext, useRef, useContext, useEffect, useState, useLayoutEffect } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { X, Expand } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';

// src/lib/cn.ts
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
var buttonVariants = cva(
  "relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        // Commands use a flat interactive color; context and status remain quiet.
        default: "border border-transparent bg-primary text-primary-foreground hover:bg-primary/90",
        secondary: "border border-border bg-secondary text-secondary-foreground hover:bg-muted",
        outline: "border border-border bg-transparent text-foreground hover:border-muted-foreground/50 hover:bg-secondary/60",
        ghost: "bg-transparent text-foreground hover:bg-secondary/60",
        destructive: "border border-critical/30 bg-critical/10 text-critical hover:border-critical/50 hover:bg-critical/15",
        // Legacy "brand" commands share the primary palette; green is reserved for success.
        brand: "border border-transparent bg-primary text-primary-foreground hover:bg-primary/90",
        ai: "border border-ai/30 bg-ai/10 text-ai hover:border-ai/50 hover:bg-ai/15",
        success: "border border-success/30 bg-success/10 text-success hover:border-success/50 hover:bg-success/15",
        /** A flat high-contrast command on dark surfaces. */
        light: "border border-transparent bg-foreground text-background hover:bg-foreground/90",
        link: "text-primary underline-offset-4 hover:underline"
      },
      size: {
        xs: "h-[var(--helios-button-xs-height,28px)] px-2.5 text-xs",
        sm: "h-[var(--helios-button-sm-height,28px)] px-3 text-xs",
        md: "h-[var(--helios-button-height,28px)] px-4 text-xs",
        lg: "h-[var(--helios-button-lg-height,44px)] px-6 text-base",
        icon: "h-[var(--helios-button-icon-size,28px)] w-[var(--helios-button-icon-size,28px)]"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "md"
    }
  }
);
var Button = forwardRef(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx(
      Comp,
      {
        ref,
        type: asChild ? void 0 : type ?? "button",
        className: cn(buttonVariants({ variant, size }), className),
        ...props
      }
    );
  }
);
Button.displayName = "Button";
var SurfaceInspectionContext = createContext(null);
function SurfaceDetailData({ detail }) {
  const inspection = useContext(SurfaceInspectionContext);
  const marker = useRef(null);
  useEffect(() => {
    const surface = marker.current?.closest("[data-data-surface]");
    if (!surface || !inspection) return;
    return inspection.register(surface, detail);
  }, [detail, inspection]);
  return /* @__PURE__ */ jsx("span", { ref: marker, hidden: true, "data-surface-detail-data": "" });
}
function SurfaceInspectButton({
  title,
  native = false,
  reserveSpace = false,
  className
}) {
  const inspection = useContext(SurfaceInspectionContext);
  const marker = useRef(null);
  const [eligible, setEligible] = useState(false);
  useEffect(() => {
    const surface = marker.current?.closest("[data-data-surface]");
    if (!surface || !inspection || native) {
      setEligible(false);
      return;
    }
    const update = () => setEligible(
      !surface.matches(
        'button, a, form, [role="button"], [role="link"], [data-native-inspection]'
      ) && !surface.closest('form, [contenteditable="true"], [data-native-modal-boundary]') && !surface.querySelector(
        'form, [contenteditable="true"], textarea, select, input:not([type="hidden"]):not([type="search"]):not([readonly]):not([disabled])'
      )
    );
    update();
    const observer = new MutationObserver(update);
    observer.observe(surface, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        "disabled",
        "readonly",
        "type",
        "contenteditable",
        "role",
        "data-native-inspection",
        "data-native-modal-boundary"
      ]
    });
    for (let ancestor = surface.parentElement; ancestor; ancestor = ancestor.parentElement) {
      observer.observe(ancestor, {
        attributes: true,
        attributeFilter: ["contenteditable", "data-native-modal-boundary"]
      });
    }
    return () => observer.disconnect();
  }, [inspection, native]);
  useLayoutEffect(() => {
    const surface = marker.current?.closest("[data-data-surface]");
    if (!surface || !eligible || !reserveSpace || !inspection || native) return;
    const flag = "data-surface-inspection-reserved";
    const padding = "--hx-inspection-base-padding";
    const minimum = "--hx-inspection-base-minimum";
    let frame = 0;
    const measure = () => {
      surface.removeAttribute(flag);
      const style = window.getComputedStyle(surface);
      surface.style.setProperty(padding, style.paddingInlineEnd || style.paddingRight || "0px");
      const min = style.minBlockSize || style.minHeight;
      surface.style.setProperty(minimum, !min || min === "auto" ? "0px" : min);
      surface.setAttribute(flag, "");
    };
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };
    measure();
    const layoutDeclarations = (style) => (style ?? "").match(/(?:^|;)\s*(?:padding(?:-[\w-]+)?|min-(?:height|block-size))\s*:[^;]*/gi)?.join(";") ?? "";
    const observer = new MutationObserver((records) => {
      if (records.some(
        (record) => record.attributeName === "class" || layoutDeclarations(record.oldValue) !== layoutDeclarations(surface.getAttribute("style"))
      ))
        schedule();
    });
    observer.observe(surface, {
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ["class", "style"]
    });
    const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    resize?.observe(surface);
    window.addEventListener("resize", schedule);
    return () => {
      observer.disconnect();
      resize?.disconnect();
      window.removeEventListener("resize", schedule);
      cancelAnimationFrame(frame);
      surface.removeAttribute(flag);
      surface.style.removeProperty(padding);
      surface.style.removeProperty(minimum);
    };
  }, [eligible, inspection, native, reserveSpace]);
  if (!inspection || native) return null;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("span", { ref: marker, hidden: true, "data-surface-inspect-marker": "" }),
    eligible ? /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "data-surface-inspect-control": "",
        "aria-label": title ? `Expand ${title}` : "Expand data panel",
        title: title ? `Expand ${title}` : "Expand data panel",
        className: cn("hx-surface-inspect-button", className),
        onClick: (event) => {
          event.stopPropagation();
          const surface = event.currentTarget.closest("[data-data-surface]");
          if (surface) inspection.inspect(surface);
        },
        children: /* @__PURE__ */ jsx(Expand, { className: "h-3.5 w-3.5", "aria-hidden": "true" })
      }
    ) : null
  ] });
}
function SurfaceInspectionScope({
  value,
  children
}) {
  return /* @__PURE__ */ jsx(SurfaceInspectionContext.Provider, { value, children });
}
var badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors focus:outline-none",
  {
    variants: {
      variant: {
        // Context is carried by text on a shared neutral surface, never a halo.
        default: "border-border bg-secondary/60 text-[hsl(var(--context-primary))]",
        success: "border-border bg-secondary/60 text-success",
        warning: "border-border bg-secondary/60 text-warning",
        critical: "border-border bg-secondary/60 text-critical",
        info: "border-border bg-secondary/60 text-[hsl(var(--context-primary))]",
        ai: "border-border bg-secondary/60 text-ai",
        brand: "border-border bg-secondary/60 text-brand",
        neutral: "border-border bg-secondary/60 text-muted-foreground",
        outline: "border-border bg-transparent text-muted-foreground"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var Badge = forwardRef(
  ({ className, variant, ...props }, ref) => /* @__PURE__ */ jsx("span", { ref, className: cn(badgeVariants({ variant }), className), ...props })
);
Badge.displayName = "Badge";
var Dialog = DialogPrimitive.Root;
var DialogTrigger = DialogPrimitive.Trigger;
var DialogClose = DialogPrimitive.Close;
var DialogPortal = DialogPrimitive.Portal;
var DialogOverlay = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Overlay,
  {
    ref,
    className: cn(
      "z-overlay fixed inset-0 bg-black/65 backdrop-blur-[6px]",
      "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
      className
    ),
    ...props
  }
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
var DialogContent = forwardRef(
  ({ className, children, hideClose = false, onOpenAutoFocus, onCloseAutoFocus, ...props }, ref) => {
    const opener = useRef(null);
    return /* @__PURE__ */ jsxs(DialogPortal, { children: [
      /* @__PURE__ */ jsx(DialogOverlay, {}),
      /* @__PURE__ */ jsxs(
        DialogPrimitive.Content,
        {
          ref,
          className: cn(
            "scheme-dark bg-popover text-popover-foreground glass-strong z-modal fixed left-1/2 top-1/2 w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto overscroll-contain rounded-2xl p-4 outline-none sm:p-6",
            // A transform-based zoom overwrites the centering translation during
            // entry. Opacity preserves containment and keyboard reachability.
            "helios-dialog-content data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
            className
          ),
          onOpenAutoFocus: (event) => {
            opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            onOpenAutoFocus?.(event);
          },
          onCloseAutoFocus: (event) => {
            onCloseAutoFocus?.(event);
            if (!event.defaultPrevented && opener.current?.isConnected) {
              event.preventDefault();
              opener.current.focus({ preventScroll: true });
            }
          },
          ...props,
          "data-native-modal-boundary": "",
          children: [
            /* @__PURE__ */ jsx(SurfaceInspectionScope, { value: null, children }),
            hideClose ? null : /* @__PURE__ */ jsx(
              DialogPrimitive.Close,
              {
                className: "text-muted-foreground hover:bg-secondary hover:text-foreground absolute right-4 top-4 rounded-full p-1 transition-colors focus-visible:outline-none",
                "aria-label": "Close",
                children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4", "aria-hidden": "true" })
              }
            )
          ]
        }
      )
    ] });
  }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;
function DialogHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn("hx-section-header mb-4 flex flex-col gap-1.5 rounded-md p-2.5", className),
      ...props
    }
  );
}
function DialogFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn("mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className),
      ...props
    }
  );
}
var DialogTitle = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Title,
  {
    ref,
    className: cn("hx-section-title text-lg font-semibold tracking-tight", className),
    ...props
  }
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
var DialogDescription = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("text-muted-foreground text-sm", className),
    ...props
  }
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export { Badge, Button, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, SurfaceDetailData, SurfaceInspectButton, SurfaceInspectionContext, SurfaceInspectionScope, badgeVariants, buttonVariants, cn };
//# sourceMappingURL=chunk-UCBSVPDO.mjs.map
//# sourceMappingURL=chunk-UCBSVPDO.mjs.map